import { Container, Graphics, Sprite, Texture } from 'pixi.js';
import type { AABB } from '../engine/Physics';
import type { LevelDef, SpikeDef } from '../types';
import { MovingPlatform } from './MovingPlatform';

const COLOR_SPIKE = 0xf44336;
const COLOR_GOAL = 0x9c27b0;
const COLOR_CHECKPOINT = 0xffd700;
const COLOR_OUTLINE = 0x000000;

function hex(color: string): number {
  return parseInt(color.replace('#', ''), 16);
}

export interface LevelAssets {
  platformHUrl?: string;
  platformVUrl?: string;
  platformMUrl?: string;
  spikeUrl?: string;
}

export class Level {
  view = new Container();
  private platformsGfx = new Graphics();
  private platformSprites = new Container();
  private movingLayer = new Container();
  private spikesGfx = new Graphics();
  private spikeSprites = new Container();
  private goalGfx = new Graphics();
  private checkpointsGfx = new Graphics();

  solids: AABB[] = [];
  spikeBoxes: AABB[] = [];
  movingPlatforms: MovingPlatform[] = [];

  constructor(public def: LevelDef, public tileSize: number, private assets: LevelAssets = {}) {
    this.view.addChild(this.platformsGfx);
    this.view.addChild(this.platformSprites);
    this.view.addChild(this.movingLayer);
    this.view.addChild(this.spikesGfx);
    this.view.addChild(this.spikeSprites);
    this.view.addChild(this.goalGfx);
    this.view.addChild(this.checkpointsGfx);
    this.build();
  }

  step(): void {
    for (const mp of this.movingPlatforms) mp.step();
  }

  private build(): void {
    for (const p of this.def.platforms) {
      const isVertical = p.h > p.w;
      const texUrl = isVertical ? this.assets.platformVUrl : this.assets.platformHUrl;

      if (texUrl) {
        const tex = Texture.from(texUrl);
        const sprite = new Sprite(tex);
        sprite.anchor.set(0, 0);
        sprite.x = p.x;
        sprite.y = p.y;
        sprite.width = p.w;
        sprite.height = p.h;
        this.platformSprites.addChild(sprite);

      } else {
        this.platformsGfx
          .rect(p.x, p.y, p.w, p.h)
          .fill({ color: hex(p.color) })
          .stroke({ color: COLOR_OUTLINE, width: 2, alignment: 1 });
      }
      this.solids.push({ x: p.x, y: p.y, w: p.w, h: p.h });
    }

    if (this.def.movingPlatforms) {
      for (const def of this.def.movingPlatforms) {
        const mp = new MovingPlatform(def, this.assets.platformMUrl);
        this.movingPlatforms.push(mp);
        this.movingLayer.addChild(mp.gfx);
        this.solids.push(mp.aabb);
      }
    }

    for (const s of this.def.spikes) {
      if (this.assets.spikeUrl) {
        this.drawSpikeSprite(s);
      } else {
        this.drawSpike(s);
      }
      this.spikeBoxes.push(this.spikeAabb(s));
    }

    const ts = this.tileSize;

    for (const c of this.def.checkpoints) {
      this.checkpointsGfx
        .rect(c.x + ts * 0.4, c.y, ts * 0.2, ts * 1.5)
        .fill({ color: 0x888888 });
      this.checkpointsGfx
        .poly([c.x + ts * 0.6, c.y, c.x + ts * 1.4, c.y + ts * 0.4, c.x + ts * 0.6, c.y + ts * 0.8])
        .fill({ color: COLOR_CHECKPOINT })
        .stroke({ color: COLOR_OUTLINE, width: 2 });
    }

    const g = this.def.goal;
    if (g) {
      this.goalGfx
        .rect(g.x, g.y, ts, ts * 1.5)
        .fill({ color: COLOR_GOAL })
        .stroke({ color: COLOR_OUTLINE, width: 2 });
    }
  }

  private drawSpikeSprite(s: SpikeDef): void {
    const ts = this.tileSize;
    const tex = Texture.from(this.assets.spikeUrl!);
    const sprite = new Sprite(tex);
    sprite.width = ts;
    sprite.height = ts;
    sprite.x = s.x + ts / 2;
    sprite.y = s.y + ts / 2;
    sprite.anchor.set(0.5);
    switch (s.dir) {
      case 'up':    sprite.rotation = 0; break;
      case 'down':  sprite.rotation = Math.PI; break;
      case 'left':  sprite.rotation = -Math.PI / 2; break;
      case 'right': sprite.rotation = Math.PI / 2; break;
    }
    this.spikeSprites.addChild(sprite);
  }

  private drawSpike(s: SpikeDef): void {
    const ts = this.tileSize;
    const g = this.spikesGfx;
    let pts: number[];
    switch (s.dir) {
      case 'up':
        pts = [s.x, s.y + ts, s.x + ts, s.y + ts, s.x + ts / 2, s.y];
        break;
      case 'down':
        pts = [s.x, s.y, s.x + ts, s.y, s.x + ts / 2, s.y + ts];
        break;
      case 'left':
        pts = [s.x + ts, s.y, s.x + ts, s.y + ts, s.x, s.y + ts / 2];
        break;
      case 'right':
        pts = [s.x, s.y, s.x, s.y + ts, s.x + ts, s.y + ts / 2];
        break;
    }
    g.poly(pts).fill({ color: COLOR_SPIKE }).stroke({ color: COLOR_OUTLINE, width: 2 });
  }

  private spikeAabb(s: SpikeDef): AABB {
    const ts = this.tileSize;
    const inset = ts * 0.1; // уменьшаем отступ для надёжного обнаружения
    return { x: s.x + inset, y: s.y + inset, w: ts - inset * 2, h: ts - inset * 2 };
  }
}
